import {FieldMappingTarget} from 'molbio/bulk-assembly/assemble-modal/revampedPerformAssemblyModalReducer';
import {
  BulkAssemblyProcedure,
  CLIENT_PROCEDURE_JSON_CURRENT_VERSION,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {AsyncBulkAssemblyValidationResults} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {OngoingTaskResponse} from 'util/TaskRunner';

export type WorklistOptions =
  | {
      worklistDestination: 'new';
      newWorklistName: string;
    }
  | {
      worklistDestination: 'existing';
      worklistId: string;
    };

export type TableOptions =
  | {
      tableDestination: 'new';
      newTableName: string;
      tagSchemaId: string;
    }
  | {
      tableDestination: 'existing';
      structuredTableId: string;
      tagSchemaId: string;
    };

export interface SaveInformation {
  folderId: string;
  tagSchemaId?: string | null;
  worklistOptions?: WorklistOptions;
  fieldMapping?: {[fieldId: string]: FieldMappingTarget};
  documentLikeId?: string;
  tableOptions?: TableOptions;
}

export interface FinalizationLocationInfo {
  fragmentSaveInfo: SaveInformation | null;
  constructSaveInfo: SaveInformation;
  primerSaveInfo: SaveInformation | null;
}

export function createAssembly({
  name,
  folderId,
  procedureJson,
  studyIds,
}: {
  name: string;
  folderId: string;
  procedureJson: BulkAssemblyProcedure;
  studyIds?: Array<string>;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: '/1/api/bulk-assemblies',
    data: {
      name,
      folderId,
      procedureJson: {version: CLIENT_PROCEDURE_JSON_CURRENT_VERSION, contents: procedureJson},
      studyIds,
    },
  });
}

export function patchBulkAssembly(assemblyId: string, procedureJson?: BulkAssemblyProcedure): Promise<{}> {
  return net.patchJSONPromise({
    url: `/1/api/bulk-assemblies/${assemblyId}`,
    data: {
      procedureJson:
        procedureJson != null
          ? {version: CLIENT_PROCEDURE_JSON_CURRENT_VERSION, contents: procedureJson}
          : undefined,
    },
  });
}

export function finalizeAssembly(
  finalizationParams: {
    bulkAssemblyId: string;
    procedureJson: BulkAssemblyProcedure;
  } & FinalizationLocationInfo
): Promise<{} | OngoingTaskResponse> {
  // returns another task if assembly is also being sent to notebook
  return Promise.resolve(
    TaskRunner.postJSONPromise({
      url: '/1/api/bulk-assemblies:finalize',
      data: {
        ...finalizationParams,
        procedureJson: {
          version: CLIENT_PROCEDURE_JSON_CURRENT_VERSION,
          contents: finalizationParams.procedureJson,
        },
      },
    })
  );
}

export function asyncValidateAssembly(
  bulkAssemblyId: string,
  procedureJson: BulkAssemblyProcedure
): Promise<AsyncBulkAssemblyValidationResults> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/bulk-assemblies/async-validate',
    data: {
      bulkAssemblyId,
      procedureJson: {
        version: CLIENT_PROCEDURE_JSON_CURRENT_VERSION,
        contents: procedureJson,
      },
    },
  });
}

export function sendBulkAssemblyToNotebook({
  bulkAssemblyId,
  documentLikeId,
  constructTableOptions,
  fragmentTableOptions,
  primerTableOptions,
}: {
  bulkAssemblyId: string;
  documentLikeId: string;
  constructTableOptions: TableOptions;
  fragmentTableOptions?: TableOptions;
  primerTableOptions?: TableOptions;
}): Promise<{}> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/bulk-assemblies/${bulkAssemblyId}:send-to-notebook`,
    data: {
      documentLikeId,
      constructTableOptions,
      fragmentTableOptions,
      primerTableOptions,
    },
  });
}
